import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { symptomsAction } from 'reduxStateSlice/symptoms';
import { NullableCommunique } from 'types/types';
import { patientAction } from 'reduxStateSlice/patient';
import { ModuleTypes } from 'types/appState';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import { RootState } from 'store/store';
import { appointmentAction } from 'reduxStateSlice/appointment';
import {
  useGetAppointmentDetailsByAppointmentId,
  useGetPatientByIdWithPharmacy,
  useGetSymptomsFromImmediatePastVisit,
} from 'hooks/useApi';
import isNil from 'lodash/isNil';
import { communiqueAction } from 'reduxStateSlice/communique';

const useAppointmentCommunique = () => {
  const communiqueData = useSelector(
    (state: RootState) => state.communique,
  ) as NullableCommunique;
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [noModuleExists, setNoModuleExists] = useState(false);
  const processedAppointmentId = useRef<number | null | undefined>(undefined);
  const dispatch = useDispatch();
  const { mutateAsync: getPatientByIdWithPharmacy } = useGetPatientByIdWithPharmacy();
  const {
    mutateAsync: getAppointmentDetailsByAppointmentId,
  } = useGetAppointmentDetailsByAppointmentId();
  const { mutateAsync: getSymptomsFromImmediatePastVisit } = useGetSymptomsFromImmediatePastVisit();

  const getAppointmentCommunique = async (data: NullableCommunique) => {
    if (!data?.patientId) {
      setIsLoading(false);
      return;
    }
    const { patientId } = data;

    try {
      const appointmentDetail = await getAppointmentDetailsByAppointmentId(
        data.appointmentId ?? 0,
      );
      const patient = await getPatientByIdWithPharmacy(patientId);
      dispatch(appointmentAction(appointmentDetail));
      dispatch(patientAction(patient));
      const appointmentCommunique = appointmentDetail?.appointment?.confirmAppointmentCommunique;
      const requestedForms = appointmentCommunique?.sendForms;
      const submissionStatus = appointmentCommunique?.intakeSubmissionStatus;
      const isHpiEnabled = requestedForms?.hpi ?? data.sendForms?.hpi ?? false;
      const isMedicalHistoryEnabled = requestedForms?.medicalHistory
        ?? data.sendForms?.medicalHistory
        ?? false;
      const shouldCompleteHpi = isHpiEnabled && !submissionStatus?.hpiSubmitted;
      const shouldCompleteMedicalHistory = isMedicalHistoryEnabled
        && !submissionStatus?.medicalHistorySubmitted;
      const shouldCompletePharmacy = isMedicalHistoryEnabled
        && !submissionStatus?.pharmacyAllergyMedicationsSubmitted;
      const pendingSendForms = {
        ...data.sendForms,
        hpi: shouldCompleteHpi,
        medicalHistory: shouldCompleteMedicalHistory,
        pharmacyAllergyMedicationsSubmitted: shouldCompletePharmacy,
      };

      if (
        data.sendForms?.hpi !== pendingSendForms.hpi
        || data.sendForms?.medicalHistory !== pendingSendForms.medicalHistory
        || data.sendForms?.pharmacyAllergyMedicationsSubmitted
          !== pendingSendForms.pharmacyAllergyMedicationsSubmitted
      ) {
        dispatch(communiqueAction({ ...data, sendForms: pendingSendForms }));
      }

      // Populate specific module
      if (shouldCompleteHpi) {
        if (communiqueData?.sendForms?.isFollowUp) {
          try {
            const pastSymptom = await getSymptomsFromImmediatePastVisit(
              data.patientId,
            );
            dispatch(symptomsAction(pastSymptom));
            dispatch(
              appStateSettingsAction({
                name: ModuleTypes.existingPatient,
                page: null,
              }),
            );
          } catch (error) {
            if (data.practiceType === 'Podiatry') {
              dispatch(
                appStateSettingsAction({
                  name: ModuleTypes.podiatry,
                  page: null,
                }),
              );
            } else {
              dispatch(
                appStateSettingsAction({
                  name: ModuleTypes.nonPodiatry,
                  page: null,
                }),
              );
            }
          }
        } else if (data.practiceType === 'Podiatry') {
          dispatch(
            appStateSettingsAction({
              name: ModuleTypes.podiatry,
              page: null,
            }),
          );
        } else {
          dispatch(
            appStateSettingsAction({
              name: ModuleTypes.nonPodiatry,
              page: null,
            }),
          );
        }
      } else if (shouldCompleteMedicalHistory) {
        dispatch(
          appStateSettingsAction({
            name: ModuleTypes.medicalHistory,
            page: null,
          }),
        );
      } else if (shouldCompletePharmacy) {
        dispatch(
          appStateSettingsAction({
            name: ModuleTypes.pharmacyMedications,
            page: null,
          }),
        );
      } else if (data.sendForms?.consentForm) {
        dispatch(
          appStateSettingsAction({
            name: ModuleTypes.consentForm,
            page: null,
          }),
        );
      } else {
        setNoModuleExists(true);
      }
      setIsLoading(false);
    } catch (error) {
      setErrorMessage('Something went wrong with getAppointmentDetailsByAppointmentId');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const appointmentId = communiqueData?.appointmentId ?? null;
    if (!isNil(communiqueData) && processedAppointmentId.current !== appointmentId) {
      processedAppointmentId.current = appointmentId;
      getAppointmentCommunique(communiqueData);
    }
  }, [communiqueData]);

  return { errorMessage, isLoading, noModuleExists };
};

export default useAppointmentCommunique;
